import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Cup } from 'iconsax-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TabellaLista from '../../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { useFunzioni } from '../../../../components/Util/featureFlags';
import { LIST_URL, creaDisciplina, getToken, mapSport, schedaUrl } from './scheda';

/*
 * Le discipline sportive stavano in un elenco compilato nel codice: aggiungerne una
 * voleva dire toccare backend, Manager e app e fare tre rilasci. Da qui il Superadmin
 * le gestisce come un'anagrafica qualsiasi.
 *
 * La riga apre la scheda della disciplina (scheda.js): li' si cambia il nome, si
 * disattiva o si elimina, e nel tab Traguardi si gestiscono i suoi traguardi.
 * Le regole su codice e discipline nuove sono spiegate in scheda.js.
 */

// Valori dei filtri a bottoni.
const TIPO_OPTIONS = ['Core', 'Storica', 'Aggiunta'];
const STATO_OPTIONS = ['Attiva', 'Disattivata'];

export const ListaDiscipline = () => {
    // Interruttore «Creazione di discipline nuove» (Impostazioni › Funzioni)
    const { sportsCatalogCreation: SPORT_CATALOG_CREATION_ENABLED } = useFunzioni();
    const navigate = useNavigate();
    const [data_json, setDataJson] = useState(null);
    // Chip a scelta multipla: all'inizio tutti accesi
    const [tipoFilter, setTipoFilter] = useState(TIPO_OPTIONS);
    const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);

    const filteredData = useMemo(
        () =>
            data_json &&
            data_json.filter(
                (sport) => filtroMultiplo(tipoFilter, TIPO_OPTIONS, sport.tipo) && filtroMultiplo(statoFilter, STATO_OPTIONS, sport.stato),
            ),
        [data_json, tipoFilter, statoFilter],
    );

    const [errorText, setErrorText] = useState('');

    // «Nuova disciplina»: una finestra con il solo nome (anche da ?nuova=1, il vecchio indirizzo della pagina)
    const location = useLocation();
    const [nuova, setNuova] = useState(() => SPORT_CATALOG_CREATION_ENABLED && new URLSearchParams(location.search).has('nuova'));
    const [nomeNuova, setNomeNuova] = useState('');
    const [erroreNuova, setErroreNuova] = useState('');
    const [creazione, setCreazione] = useState(false);
    const apriNuova = () => { setNomeNuova(''); setErroreNuova(''); setNuova(true); };
    const chiudiNuova = () => { if (!creazione) setNuova(false); };
    const crea = async (e) => {
        if (e) e.preventDefault();
        setCreazione(true);
        setErroreNuova('');
        const esito = await creaDisciplina(nomeNuova, data_json);
        setCreazione(false);
        if (esito.error) { setErroreNuova(esito.error); return; }
        // Creata: si va nella sua scheda, da cui si aggiungono i traguardi
        navigate(schedaUrl(esito.code));
    };

    const loadData = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        const result = await new MyApiClient().getAdminSports(token);

        if (!result || result.error) {
            setErrorText(result?.error ?? 'Impossibile leggere il catalogo delle discipline');
            setDataJson([]);
            return;
        }

        setDataJson(result.map(mapSport));
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const data_column = [
        {
            Header: 'Nome',
            Footer: 'Nome',
            accessor: 'label',
        },
        {
            Header: 'Tipo',
            Footer: 'Tipo',
            accessor: 'tipo',
            disableGlobalFilter: true,
            Cell: ({ row }) => (
                <div className="d-flex align-items-center">
                    <div>
                        {row.original.tipo}
                        {row.original.core && (
                            <small className="d-block">Ha livelli, ruoli e traguardi dedicati</small>
                        )}
                    </div>
                </div>
            ),
        },
        {
            Header: 'Traguardi',
            Footer: 'Traguardi',
            accessor: 'traguardi',
            disableGlobalFilter: true,
            Cell: ({ row }) =>
                row.original.traguardi === undefined ? (
                    '-'
                ) : (
                    <Link to={schedaUrl(row.original.code, 'traguardi')}>
                        {row.original.traguardi}
                        {row.original.traguardiDisattivati > 0 && (
                            <small className="d-block text-muted">+{row.original.traguardiDisattivati} disattivati</small>
                        )}
                    </Link>
                ),
        },
        {
            Header: 'Stato',
            Footer: 'Stato',
            accessor: 'stato',
            disableGlobalFilter: true,
            Cell: ({ row }) => (
                <div className="d-flex align-items-center">
                    <i className={`fa fa-circle me-1 ${row.original.active ? 'text-success' : 'text-danger'}`}></i>
                    <div>{row.original.stato}</div>
                </div>
            ),
        },
    ];

    return (
        <>
            <Modal show={!!errorText} onHide={() => setErrorText('')} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Errore durante l'operazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>{errorText}</Modal.Body>
            </Modal>

            <Modal show={nuova} onHide={chiudiNuova} centered>
                <Form onSubmit={crea}>
                    <Modal.Header closeButton>
                        <Modal.Title>Nuova disciplina</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Label htmlFor="nuova-disciplina-nome">Nome</Form.Label>
                        <Form.Control id="nuova-disciplina-nome" autoFocus value={nomeNuova} maxLength={60} onChange={(e) => setNomeNuova(e.target.value)} />
                        <Form.Text className="text-muted">Per esempio: Subacquea, Voga in piedi.</Form.Text>
                        {erroreNuova && <div className="alert alert-danger mt-3 mb-0">{erroreNuova}</div>}
                        <p className="sid-nota-leggibile mt-4 mb-0">
                            Le discipline aggiunte da qui non hanno livelli tecnici, ruoli di squadra né sfide in Arena,
                            e usano l'icona generica. I traguardi si aggiungono dopo il salvataggio, dal tab Traguardi
                            della scheda.
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="btn btn-light btn-add" onClick={chiudiNuova} disabled={creazione}>Annulla</Button>
                        <Button type="submit" className="btn btn-primary btn-add" disabled={creazione || nomeNuova.trim().length < 2}>
                            {creazione ? 'Creazione…' : 'Crea disciplina'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <TabellaLista
                openColumn={'label'}
                openRow={(row) => navigate(schedaUrl(row.code))}
                cardTitle={'Discipline sportive'}
                pageList={true}
                hasSearch={true}
                searchPlaceholder={'Cerca per nome'}
                hasFilter={false}
                filterControl={
                    <div className="sid-chip-filters">
                        <ChipFilter label="Tipo" options={TIPO_OPTIONS} value={tipoFilter} onChange={setTipoFilter} multiple />
                        <ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
                    </div>
                }
                hasAddMoreAsModal={SPORT_CATALOG_CREATION_ENABLED}
                modalCallBack={apriNuova}
                labelAddMore={'Nuova disciplina'}
                headerButtons={
                    <Link to={`${LIST_URL}/GLOBAL/traguardi`} className="btn btn-light btn-sm btn-add">
                        <Cup size="18" />
                        <span className="ms-2">Traguardi di tutte le discipline</span>
                    </Link>
                }
                data_column={data_column}
                data_json={filteredData}
                sort_by={'label'}
                sort_type={'asc'}
            />
        </>
    );
};

export default ListaDiscipline;
